import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const links = [
    { label: t.nav.programs, href: "#programs", id: "programs" },
    { label: t.nav.journey, href: "#journey", id: "journey" },
    { label: t.nav.scale, href: "#scale", id: "scale" },
    { label: t.nav.leadership, href: "#leadership", id: "leadership" },
  ];

  const scrollTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="main-nav"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-ink/70 border-b border-line"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" onClick={(e) => scrollTo(e, "#top")} data-testid="nav-logo" className="font-mono text-sm tracking-[0.2em] text-paper hover:text-volt">
          KB<span className="text-volt">.</span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              onClick={(e) => scrollTo(e, l.href)}
              data-testid={`nav-link-${l.id}`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-dim hover:text-volt"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2 font-mono text-xs tracking-[0.2em]">
            <button
              onClick={() => setLang("en")}
              data-testid="lang-toggle-en"
              className={lang === "en" ? "text-volt" : "text-dim hover:text-paper"}
            >
              EN
            </button>
            <span className="text-line">/</span>
            <button
              onClick={() => setLang("de")}
              data-testid="lang-toggle-de"
              className={lang === "de" ? "text-volt" : "text-dim hover:text-paper"}
            >
              DE
            </button>
          </div>
          <a
            href="#contact"
            onClick={(e) => scrollTo(e, "#contact")}
            data-testid="nav-contact-btn"
            className="font-mono text-xs uppercase tracking-[0.2em] bg-volt text-ink px-5 py-2.5 hover:bg-paper"
          >
            {t.nav.contact}
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
            className="md:hidden text-paper hover:text-volt p-1"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
            className="md:hidden border-t border-line bg-ink/95 backdrop-blur-md overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={(e) => scrollTo(e, l.href)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="font-mono text-sm uppercase tracking-[0.2em] text-dim hover:text-volt"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
